import type { UpdateValue } from '@library/types'
import deepEqual from 'fast-deep-equal'
import { useEffect, useRef, useState } from 'react'

import { useUnmountEffect } from './useUnmountEffect'

/**
 * Позволяет использовать внешнее значение как актуальное состояние
 *
 * @param syncOnce - требуется ли синхронизироваться один раз, а не поддерживать синхронизацию
 * @param isReadyToSync - способ проверить, когда значение готово для синхронизации: получили из api, провалидировали и т.д.
 */
export const useSyncState = <S>(
  valueOrFunction: S | (() => S),
  syncOnce: boolean = false,
  isReadyToSync?: (upcomingValue: S, prevValue?: S) => boolean,
): [S, UpdateValue<S>] => {
  const [state, setState] = useState(valueOrFunction)
  const wasSynced = useRef(false)

  useEffect(() => {
    setState(prev => {
      if (wasSynced.current && syncOnce) return prev

      const value = valueOrFunction instanceof Function ? valueOrFunction() : valueOrFunction
      if (deepEqual(prev, value)) return prev

      if (isReadyToSync && !isReadyToSync(value, prev)) return prev

      wasSynced.current = true

      return value
    })
  }, [isReadyToSync, syncOnce, valueOrFunction])

  useUnmountEffect(() => {
    wasSynced.current = false
  })

  return [state, setState]
}
